'use client';

import { useEffect, useRef, useState } from 'react';

import { getProductBendLines, type ConfiguratorState } from './lib/configurator-math';
import { buildSceneObjects } from './plate-preview-3d';

type ThreeModule = typeof import('three');

function disposeObject(THREE: ThreeModule, object: import('three').Object3D) {
  object.traverse(child => {
    const mesh = child as import('three').Mesh;
    mesh.geometry?.dispose();

    const material = mesh.material;
    if (Array.isArray(material)) {
      material.forEach(item => item.dispose());
    } else {
      material?.dispose();
    }
  });
}

function centerObject(THREE: ThreeModule, object: import('three').Object3D) {
  object.updateMatrixWorld(true);

  const box = new THREE.Box3().setFromObject(object);
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  object.position.sub(center);
  object.updateMatrixWorld(true);

  return Math.max(size.x, size.y, size.z, 1);
}

function frameOrthographicCamera(
  THREE: ThreeModule,
  camera: import('three').OrthographicCamera,
  object: import('three').Object3D,
  aspect: number,
  padding = 1.28,
) {
  object.updateMatrixWorld(true);
  camera.updateMatrixWorld(true);

  const box = new THREE.Box3().setFromObject(object);
  const { min, max } = box;
  const corners = [
    new THREE.Vector3(min.x, min.y, min.z),
    new THREE.Vector3(min.x, min.y, max.z),
    new THREE.Vector3(min.x, max.y, min.z),
    new THREE.Vector3(min.x, max.y, max.z),
    new THREE.Vector3(max.x, min.y, min.z),
    new THREE.Vector3(max.x, min.y, max.z),
    new THREE.Vector3(max.x, max.y, min.z),
    new THREE.Vector3(max.x, max.y, max.z),
  ].map(corner => corner.applyMatrix4(camera.matrixWorldInverse));

  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  let minZ = Number.POSITIVE_INFINITY;
  let maxZ = Number.NEGATIVE_INFINITY;

  corners.forEach(corner => {
    minX = Math.min(minX, corner.x);
    maxX = Math.max(maxX, corner.x);
    minY = Math.min(minY, corner.y);
    maxY = Math.max(maxY, corner.y);
    minZ = Math.min(minZ, corner.z);
    maxZ = Math.max(maxZ, corner.z);
  });

  const safeAspect = Math.max(aspect, 0.2);
  const boundsWidth = Math.max(maxX - minX, 1);
  const boundsHeight = Math.max(maxY - minY, 1);
  const viewHeight = Math.max(boundsHeight, boundsWidth / safeAspect) * padding;
  const viewWidth = viewHeight * safeAspect;
  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;
  const depth = Math.max(maxZ - minZ, boundsWidth, boundsHeight, 1);

  camera.left = centerX - viewWidth / 2;
  camera.right = centerX + viewWidth / 2;
  camera.top = centerY + viewHeight / 2;
  camera.bottom = centerY - viewHeight / 2;
  const nearDistance = Math.max(0.1, -maxZ - depth * 3);
  const farDistance = Math.max(nearDistance + 1, -minZ + depth * 3);

  camera.near = nearDistance;
  camera.far = farDistance;
  camera.updateProjectionMatrix();
}

function cnPreviewRoot(variant: 'default' | 'card') {
  return variant === 'card'
    ? 'relative h-full min-h-0 w-full overflow-hidden rounded-xl'
    : 'relative h-full min-h-[210px] w-full overflow-hidden rounded-xl';
}
function softenCardModel(THREE: ThreeModule, object: import('three').Object3D) {
  object.traverse(child => {
    if (child instanceof THREE.LineSegments) {
      const material = child.material as import('three').LineBasicMaterial;
      material.color.set('#252525');
      material.opacity = 0.24;
      material.transparent = true;
      return;
    }

    if (!(child instanceof THREE.Mesh)) return;

    child.castShadow = false;
    child.receiveShadow = false;
    const materials = Array.isArray(child.material) ? child.material : [child.material];
    materials.forEach((material, index) => {
      if (!(material instanceof THREE.MeshStandardMaterial)) return;
      material.color.set(index === 0 ? '#303033' : '#8c8c8c');
      material.roughness = 0.9;
      material.metalness = 0.02;
      material.envMapIntensity = 0.15;
      material.needsUpdate = true;
    });
  });
}

const MODEL_ROTATION_Z: Record<string, number> = {
  'lisonex-upinka-01-three': Math.PI,
};

export function ProductTypePreview3D({
  config,
  variant = 'default',
}: {
  config: ConfiguratorState;
  variant?: 'default' | 'card';
}) {
  const mountRef = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let cleanup = () => {};

    async function setup() {
      const THREE = await import('three');
      const mount = mountRef.current;
      if (!mount || cancelled) return;

      const scheduledFrames: number[] = [];
      const scheduledTimers: number[] = [];

      mount.innerHTML = '';
      const scene = new THREE.Scene();
      const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, -5000, 5000);
      const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.setClearColor(0xffffff, 0);
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = variant === 'card' ? 1.04 : 1.12;
      mount.appendChild(renderer.domElement);

      if (variant === 'card') {
        scene.add(new THREE.AmbientLight('#ffffff', 2.6));
        const keyLight = new THREE.DirectionalLight('#ffffff', 1.15);
        keyLight.position.set(260, 220, 420);
        scene.add(keyLight);
        const fillLight = new THREE.DirectionalLight('#fff2ed', 0.42);
        fillLight.position.set(-220, -160, 180);
        scene.add(fillLight);
      } else {
        scene.add(new THREE.HemisphereLight('#ffffff', '#d9d4d0', 2.25));
        const keyLight = new THREE.DirectionalLight('#ffffff', 2);
        keyLight.position.set(-240, -320, 430);
        scene.add(keyLight);
        const rimLight = new THREE.DirectionalLight('#ffe1d6', 0.95);
        rimLight.position.set(300, 220, 160);
        scene.add(rimLight);
      }

      const renderConfig =
        variant === 'card'
          ? {
              ...config,
              thicknessMm: Math.min(config.thicknessMm, 4),
              holeDiameterMm: Math.max(16, Math.min(config.holeDiameterMm, 24)),
              holeDiameters: config.holeDiameters?.map(diameter =>
                Math.max(16, Math.min(diameter, 24)),
              ),
            }
          : config;
      const shouldApplyBends = getProductBendLines(renderConfig).length > 0;
      const model = buildSceneObjects(THREE, renderConfig, {
        holeVisualScale: variant === 'card' ? 0.78 : 1,
        applyBends: shouldApplyBends,
        hideBendGuidesWhenApplied: true,
      });
      if (variant === 'card') softenCardModel(THREE, model);
      model.rotation.z = MODEL_ROTATION_Z[config.productTypeId] ?? 0;
      scene.add(model);
      const modelSize = centerObject(THREE, model);
      const distance = modelSize * (variant === 'card' ? 3.4 : 3.6);
      if (variant === 'card') {
        camera.position.set(2.5, 2, 5).normalize().multiplyScalar(distance);
      } else {
        const yaw = 0.72;
        const pitch = 0.45;
        camera.position.set(
          Math.sin(yaw) * Math.cos(pitch) * distance,
          Math.cos(yaw) * Math.cos(pitch) * distance,
          Math.sin(pitch) * distance,
        );
      }
      camera.lookAt(0, 0, 0);

      const render = () => {
        const width = Math.max(mount.clientWidth, 1);
        const height = Math.max(mount.clientHeight, 1);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        renderer.setSize(width, height);
        frameOrthographicCamera(
          THREE,
          camera,
          model,
          width / height,
          variant === 'card' ? 1.18 : 1.24,
        );
        renderer.render(scene, camera);
      };

      const schedulePostLayoutRender = () => {
        const run = () => {
          if (!cancelled && mount.isConnected) render();
        };
        scheduledFrames.push(
          window.requestAnimationFrame(() => {
            run();
            scheduledFrames.push(window.requestAnimationFrame(run));
          }),
        );
        scheduledTimers.push(window.setTimeout(run, 120), window.setTimeout(run, 360));
      };

      const observer = new ResizeObserver(render);
      observer.observe(mount);
      render();
      schedulePostLayoutRender();
      setReady(true);

      cleanup = () => {
        scheduledFrames.forEach(frame => window.cancelAnimationFrame(frame));
        scheduledTimers.forEach(timer => window.clearTimeout(timer));
        observer.disconnect();
        disposeObject(THREE, model);
        renderer.dispose();
        renderer.domElement.remove();
      };
    }

    setup();

    return () => {
      cancelled = true;
      cleanup();
    };
  }, [config, variant]);

  return (
    <div className={cnPreviewRoot(variant)} aria-label="3D nahlad typu vyrobku">
      <div
        className={
          variant === 'card' ? 'absolute inset-0 opacity-35' : 'absolute inset-0 opacity-70'
        }
        style={{
          backgroundImage:
            'linear-gradient(#f0f0f0 1px, transparent 1px), linear-gradient(90deg, #f0f0f0 1px, transparent 1px)',
          backgroundPosition: variant === 'card' ? 'center 12px' : 'center center',
          backgroundSize: variant === 'card' ? '17px 17px' : '18px 18px',
        }}
      />
      {!ready && <div className="absolute inset-[18%] animate-pulse rounded-xl bg-[#f4f4f4]" />}
      <div ref={mountRef} className="relative h-full w-full" />
    </div>
  );
}
